/**
 * Right-pane preview shown alongside Step 2 of the BriefCreatorOverlay.
 *
 * Renders whichever intent path is active:
 *   - synthesizedBrief → 8 section cards from a persisted Brief
 *   - adDraft           → confidence-tagged sections from POST /briefs/from-ad
 *   - template          → prefilled section preview
 *   - seedIdea          → idea card with PDO factors
 *   - empty fallback    → "pick an intent to see preview"
 */

import { Sparkles } from 'lucide-react';
import type { DraftState } from './types';
import { ConfidenceBadge } from './ConfidenceBadge';

const SECTION_LABELS: Record<string, string> = {
  objective: 'Objective',
  audience: 'Audience',
  insight: 'Insight',
  brand_voice: 'Brand Voice',
  message: 'Message',
  creative_context: 'Creative Context',
  mandatories: 'Mandatories',
  success_metrics: 'Success Metrics',
};

const SECTION_ORDER = [
  'objective',
  'audience',
  'insight',
  'brand_voice',
  'message',
  'creative_context',
  'mandatories',
  'success_metrics',
];

interface Props {
  state: DraftState;
  isStreaming?: boolean;
}

export function DraftPreviewPanel({ state, isStreaming }: Props) {
  return (
    <aside className="flex flex-col h-full border-l border-slate-200 bg-slate-50/40">
      <div className="px-4 py-3 border-b border-slate-200 bg-white">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Draft Preview
        </div>
        <div className="mt-0.5 text-sm font-semibold text-slate-900 truncate">
          {state.name || 'Untitled brief'}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {isStreaming && (
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-purple animate-pulse" />
            Generating draft…
          </div>
        )}

        {state.intent === null && !isStreaming && <EmptyState />}

        {state.adDraft && <FromAdSections draft={state.adDraft} />}
        {state.synthesizedBrief && (
          <SynthesizedSections sections={state.synthesizedBrief.sections} />
        )}
        {state.template && <TemplateSections template={state.template} />}
        {state.seedIdea && !state.synthesizedBrief && !state.adDraft && (
          <SeedIdeaCard
            title={state.seedIdea.title}
            premise={state.seedIdea.premise}
          />
        )}
      </div>
    </aside>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-4">
      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
        <Sparkles size={18} />
      </div>
      <p className="mt-3 text-[12px] text-slate-500 max-w-xs leading-snug">
        Pick an intent on the left to see your draft brief assemble here.
      </p>
    </div>
  );
}

function SeedIdeaCard({ title, premise }: { title: string; premise: string }) {
  return (
    <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700 mb-1">
        Seed idea
      </div>
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <p className="text-[12px] text-slate-600 mt-1 leading-snug">{premise}</p>
    </div>
  );
}

function FromAdSections({
  draft,
}: {
  draft: NonNullable<DraftState['adDraft']>;
}) {
  return (
    <>
      {SECTION_ORDER.map((key) => {
        const entry = draft.sections[key];
        if (!entry) return <SectionRow key={key} label={SECTION_LABELS[key]} content={null} confidence={0} />;
        return (
          <SectionRow
            key={key}
            label={SECTION_LABELS[key]}
            content={entry.content}
            confidence={entry.confidence}
          />
        );
      })}
      {draft.inferred_from_signals && draft.inferred_from_signals.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            Inferred from
          </div>
          <ul className="text-[11px] text-slate-600 space-y-0.5">
            {draft.inferred_from_signals.map((s, i) => (
              <li key={i}>• {s}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function SynthesizedSections({
  sections,
}: {
  sections: Record<string, Record<string, unknown>>;
}) {
  return (
    <>
      {SECTION_ORDER.map((key) => (
        <SectionRow
          key={key}
          label={SECTION_LABELS[key]}
          content={sections[key] ?? null}
          confidence={null}
        />
      ))}
    </>
  );
}

function TemplateSections({
  template,
}: {
  template: NonNullable<DraftState['template']>;
}) {
  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Template
        </div>
        <div className="text-sm font-semibold text-slate-900 mt-0.5">{template.name}</div>
        <p className="text-[12px] text-slate-600 mt-1 leading-snug">{template.description}</p>
      </div>
      {SECTION_ORDER.map((key) => {
        const content = template.prefill_sections[key] as Record<string, unknown> | undefined;
        return (
          <SectionRow
            key={key}
            label={SECTION_LABELS[key]}
            content={content ?? null}
            confidence={null}
          />
        );
      })}
    </>
  );
}

function SectionRow({
  label,
  content,
  confidence,
}: {
  label: string;
  content: Record<string, unknown> | null;
  confidence: number | null;
}) {
  const empty = content == null || Object.keys(content).length === 0;
  const summary = empty ? '' : summarizeSection(content);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {label}
        </div>
        {confidence !== null && <ConfidenceBadge value={confidence} />}
      </div>
      <div className="text-[12px] text-slate-700 mt-1 leading-snug">
        {empty ? (
          <span className="text-slate-400 italic">— Not yet drafted —</span>
        ) : (
          summary
        )}
      </div>
    </div>
  );
}

function summarizeSection(content: Record<string, unknown>): string {
  // Pick the most informative fields per section, falling back to the
  // first scalar string we can find.
  const candidates = [
    'statement',
    'one_liner',
    'tension',
    'tone',
    'hook_direction',
    'primary_text',
    'visual_preferences',
    'compliance',
  ];
  for (const k of candidates) {
    const v = content[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  // Fall back to first stringy value
  for (const v of Object.values(content)) {
    if (typeof v === 'string' && v.trim()) return v;
    if (Array.isArray(v) && v.length > 0) {
      return v
        .slice(0, 3)
        .map((x) => String(x))
        .join(', ');
    }
  }
  return '—';
}
